import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';

export interface NotionStatus {
  connected: boolean;
  hasMapping: boolean;
}

export interface GmailStatus {
  connected: boolean;
  email?: string;
}

interface UseIntegrationStatusProps {
  refreshFounders: () => Promise<void>;
}

export function useIntegrationStatus({ refreshFounders }: UseIntegrationStatusProps) {
  const [notionStatus, setNotionStatus] = useState<NotionStatus | null>(null);
  const [gmailStatus, setGmailStatus] = useState<GmailStatus | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Check Notion connection status on mount and periodically
  useEffect(() => {
    const checkNotionStatus = async () => {
      try {
        const status = await apiService.getNotionStatus();
        setNotionStatus({
          connected: status.connected || false,
          hasMapping: status.hasMapping ?? !!status.pageId,
        });
      } catch (error) {
        console.error("Error checking Notion status:", error);
        setNotionStatus(null);
      }
    };

    checkNotionStatus();
    const interval = setInterval(checkNotionStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Check Gmail connection status on mount and periodically
  useEffect(() => {
    const checkGmailStatus = async () => {
      try {
        const status = await apiService.getGmailStatus();
        setGmailStatus({
          connected: status.connected || false,
          email: status.profile?.email,
        });
      } catch (error) {
        console.error("Error checking Gmail status:", error);
        setGmailStatus(null);
      }
    };

    checkGmailStatus();
    const interval = setInterval(checkGmailStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleGmailConnect = useCallback(() => {
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3001/api";
    window.location.href = `${apiUrl}/gmail/connect`;
  }, []);

  const handleNotionSync = useCallback(async () => {
    if (syncing) return;

    setSyncing(true);
    try {
      await apiService.triggerNotionSync(true);
      await refreshFounders();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.message ||
        "Failed to sync from Notion";
      alert(`❌ Sync failed: ${errorMessage}`);
    } finally {
      setSyncing(false);
    }
  }, [syncing, refreshFounders]);

  return {
    notionStatus,
    gmailStatus,
    syncing,
    handleGmailConnect,
    handleNotionSync,
  };
}
